(() => {
  "use strict";

  const GIFT_WRAP_HANDLE = "gift-wrapping";
  let GIFT_WRAP_VARIANT_ID = null;

  const CART_SECTIONS = [
    // Cart page sections (Dawn & many forks)
    "main-cart-items",
    "cart-items",
    "cart-footer",
    // Optional extras if your theme exposes them:
    "cart-recommendations",
    "cart-live-region-text",
    "cart-icon-bubble",
    // If your drawer is a section, include it too:
    // "cart-drawer"
  ];  

  /* ========= Helpers inserted just below constants ========= */

  // Return the quantity of the main cart item for a given variantId
  function getMainQtyForVariant(variantId, cart) {
    const main = cart.items.find(i => i.handle !== GIFT_WRAP_HANDLE && String(i.variant_id) === String(variantId));
    return main?.quantity || 1;
  }

  // Ensure each gift-wrap line matches its linked main item's qty
  async function reconcileGiftWrapQty(cart) {
    const updates = [];
    cart.items.forEach(item => {
      if (item.handle === GIFT_WRAP_HANDLE && item.properties?._giftwrap_for_variant) {
        const mainQty = getMainQtyForVariant(String(item.properties._giftwrap_for_variant), cart);
        if (item.quantity !== mainQty) {
          updates.push(fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: item.key, quantity: mainQty })
          }));
        }
      }
    });
    if (updates.length) await Promise.all(updates);
  }

  /* ========================================================= */

  // STEP 1 — Fetch Gift Wrap Variant ID
  fetch(`/products/${GIFT_WRAP_HANDLE}.js`)
    .then(r => r.json())
    .then(product => {
      GIFT_WRAP_VARIANT_ID = product?.variants?.[0]?.id;
      if (GIFT_WRAP_VARIANT_ID) {
        initGiftWrapCheckboxes();
        initAutoGiftWrapRemoval();
        setTimeout(cleanupOrphanedGiftWraps, 1000);
      }
    })
    .catch(err => console.error("Gift wrap product fetch failed:", err));

  // 🔄 Loader utility
  function toggleLoader(checkbox, isOn) {
    if (!checkbox) return;

    checkbox.disabled = isOn;
    checkbox.classList.toggle("is-loading", isOn);

    let loader = checkbox.parentElement.querySelector(".giftwrap-loader");
    if (isOn) {
      if (!loader) {
        loader = document.createElement("span");
        loader.className = "giftwrap-loader";
        loader.innerHTML = `
          <svg class="spin" width="14" height="14" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" stroke="#433731" stroke-width="2" fill="none" opacity="0.2"/>
            <path d="M22 12a10 10 0 0 1-10 10" stroke="#433731" stroke-width="2" fill="none"/>
          </svg>`;
        checkbox.parentElement.appendChild(loader);
      }
    } else {
      loader?.remove();
    }
  }

  // STEP 2 — Setup checkboxes
  function initGiftWrapCheckboxes(skipSync = false) {
    const checkboxes = document.querySelectorAll(".giftwrap-checkbox");
    console.log(`🔄 Initializing ${checkboxes.length} gift wrap checkboxes`);

    checkboxes.forEach(cb => {
      if (cb.dataset.listenerBound) cb.removeEventListener("change", handleGiftWrapToggle);
      cb.dataset.listenerBound = "true";
      cb.disabled = false;
      cb.addEventListener("change", handleGiftWrapToggle);
    });

    if (!skipSync) syncCheckboxStates();
  }

  // STEP 3 — Sync checkbox state
  let isSyncing = false;

  async function syncCheckboxStates(syncingIds = []) {
    if (isSyncing) return console.log("⏳ Sync already running, skipping duplicate call");
    isSyncing = true;

    try {
      console.log("🔄 Syncing checkbox states with cart...");

      // Target only syncing checkboxes (or all if none specified)
      const targetCheckboxes = syncingIds.length
        ? document.querySelectorAll(syncingIds.map(id => `.giftwrap-checkbox[data-variant-id="${id}"]`).join(", "))
        : document.querySelectorAll(".giftwrap-checkbox");

      targetCheckboxes.forEach(cb => toggleLoader(cb, true));

      const cart = await fetch("/cart.js").then(r => r.json());

      document.querySelectorAll(".giftwrap-checkbox").forEach(cb => {
        cb.checked = false;
        const messageBlock = document.querySelector(`#giftwrap-message-${cb.dataset.variantId}-${cb.dataset.lineIndex}`);
        if (messageBlock) {
          messageBlock.classList.remove("active");
          messageBlock.style.display = "none";
        }
      });

      cart.items.forEach(item => {
        if (item.handle === GIFT_WRAP_HANDLE && item.properties?._giftwrap_for_variant) {
          document
            .querySelectorAll(`.giftwrap-checkbox[data-variant-id="${item.properties._giftwrap_for_variant}"]`)
            .forEach(cb => (cb.checked = true));
        }
      });

      // 🔁 Keep gift-wrap qty aligned after every sync
      await reconcileGiftWrapQty(cart);
    } catch (err) {
      console.error("❌ Sync failed:", err);
    } finally {
      document.querySelectorAll(".giftwrap-checkbox").forEach(cb => toggleLoader(cb, false));
      isSyncing = false;
    }
  }

  // STEP 4 — Handle checkbox toggle
  async function handleGiftWrapToggle(e) {
    const checkbox = e.target;
    const variantId = checkbox.dataset.variantId;
    const lineIndex = checkbox.dataset.lineIndex;
    const messageBlock = document.querySelector(`#giftwrap-message-${variantId}-${lineIndex}`);

    console.log(`🎁 Checkbox toggled for variant ${variantId}, checked: ${checkbox.checked}`);

    if (checkbox.checked) {
      if (messageBlock) {
        messageBlock.style.display = "block";
        messageBlock.classList.add("active");
      }
    } else {
      if (messageBlock) {
        messageBlock.style.display = "none";
        messageBlock.classList.remove("active");
      }

      console.log(`🗑️ Removing gift wrap for variant ${variantId}`);
      const cart = await fetch("/cart.js").then(r => r.json());
      const existingWrap = cart.items.find(
        i => i.handle === GIFT_WRAP_HANDLE && i.properties?._giftwrap_for_variant == variantId
      );

      if (existingWrap) {
        toggleLoader(checkbox, true);

        await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: existingWrap.key, quantity: 0 })
        });

        await syncCheckboxStates([variantId]);
        refreshCart();

        toggleLoader(checkbox, false);
      } else {
        console.log(`⚠️ No gift wrap found for variant ${variantId}`);
      }
    }
  }

  // STEP 5 — Add gift wrap on button click
  if (!window.__giftwrapSubmitBound) {
    window.__giftwrapSubmitBound = true;
    document.addEventListener("click", async (e) => {
      if (!e.target.classList.contains("giftwrap-submit-btn")) return;

      const btn = e.target;
      const variantId = btn.dataset.variantId;
      const productHandle = btn.dataset.productHandle;
      const lineIndex = btn.dataset.lineIndex;
      const parentItem = btn.closest(".cart__item");
      const textarea = document.querySelector(`#giftwrap-textarea-${variantId}-${lineIndex}`);
      const messageBlock = document.querySelector(`#giftwrap-message-${variantId}-${lineIndex}`);
      const message = textarea ? textarea.value.trim() : "";
      const productTitle = parentItem?.querySelector(".cart__item__title a")?.textContent?.trim() || "Product";

      if (!GIFT_WRAP_VARIANT_ID) return;

      btn.disabled = true;
      btn.textContent = "Saving...";

      const cart = await fetch("/cart.js").then(r => r.json());
      const existingWrap = cart.items.find(
        item => item.handle === GIFT_WRAP_HANDLE && item.properties?._giftwrap_for_variant == variantId
      );

      // 🔢 Use the main product's current quantity
      const mainQty = getMainQtyForVariant(variantId, cart);

      const payload = existingWrap
        ? {
            id: existingWrap.key,
            quantity: mainQty,
            properties: {
              ...existingWrap.properties,
              "For": productTitle,
              "Message": message,
              "_giftwrap_for_variant": variantId,
              "_giftwrap_for_handle": productHandle,
              "_giftwrap_message": message
            }
          }
        : {
            items: [
              {
                id: GIFT_WRAP_VARIANT_ID,
                quantity: mainQty,
                properties: {
                  "For": productTitle,
                  "Message": message,
                  "_giftwrap_for_variant": variantId,
                  "_giftwrap_for_handle": productHandle,
                  "_giftwrap_message": message
                }
              }
            ]
          };

      const url = existingWrap ? "/cart/change.js" : "/cart/add.js";

      try {
        const res = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        });

        if (!res.ok) throw new Error("Failed to save gift wrap");

        // Reconcile once more, then refresh
        const latest = await fetch("/cart.js").then(r => r.json());
        await reconcileGiftWrapQty(latest);

        showSuccessMessage(btn, messageBlock, message);
        refreshCart();
      } catch (err) {
        console.error(err);
        btn.disabled = false;
        btn.textContent = "Save Message & Add Gift Wrap";
        const errorMsg = document.createElement("div");
        errorMsg.className = "giftwrap-error-message";
        errorMsg.textContent = "Failed to add gift wrap. Please try again.";
        messageBlock.appendChild(errorMsg);
        setTimeout(() => errorMsg.remove(), 3000);
      }
    });
  }

  function showSuccessMessage(btn, messageBlock, message) {
    messageBlock.classList.remove("active");
    messageBlock.style.display = "none";

    const successDiv = document.createElement("div");
    successDiv.className = "giftwrap-success-message";
    successDiv.innerHTML = `
      <div class="giftwrap-success-content">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <circle cx="10" cy="10" r="10" fill="#433731"/>
          <path d="M6 10L8.5 12.5L14 7" stroke="white" stroke-width="2" stroke-linecap="round"/>
        </svg>
        <div>
          <strong>Gift message added!</strong>
          ${message ? `<p class="giftwrap-success-preview">"${message}"</p>` : ""}
        </div>
      </div>`;
    messageBlock.parentElement.insertBefore(successDiv, messageBlock.nextSibling);
    btn.disabled = false;
    btn.textContent = "Save Message & Add Gift Wrap";
    setTimeout(() => successDiv.remove(), 4000);
  }

  // STEP 6 — Auto remove wrap when main product removed
  function initAutoGiftWrapRemoval() {
    document.addEventListener("click", (e) => {
      const removeBtn = e.target.closest(".cart__item__remove");
      if (!removeBtn) return;

      const parentItem = removeBtn.closest(".cart__item");
      const productHandle = parentItem?.dataset.productHandle;
      const variantEl = parentItem?.querySelector("[data-variant-id]");
      const productVariantId = variantEl?.dataset.variantId;

      if (productHandle === GIFT_WRAP_HANDLE) return;

      setTimeout(async () => {
        const cart = await fetch("/cart.js").then(r => r.json());
        const linkedWrap = cart.items.find(
          item =>
            item.handle === GIFT_WRAP_HANDLE &&
            (item.properties._giftwrap_for_variant == productVariantId ||
             item.properties._giftwrap_for_handle === productHandle)
        );

        if (linkedWrap) {
          await fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: linkedWrap.key, quantity: 0 })
          });
        }

        await cleanupOrphanedGiftWraps();
        refreshCart();
      }, 800);
    });
  }

  // STEP 7 — Cleanup orphan wraps
  async function cleanupOrphanedGiftWraps() {
    try {
      const cart = await fetch("/cart.js").then(r => r.json());
      const productVariantIds = cart.items.filter(i => i.handle !== GIFT_WRAP_HANDLE).map(i => String(i.variant_id));
      const productHandles = cart.items.filter(i => i.handle !== GIFT_WRAP_HANDLE).map(i => i.handle);

      const orphanedWraps = cart.items.filter(
        item =>
          item.handle === GIFT_WRAP_HANDLE &&
          ((!item.properties._giftwrap_for_variant && !item.properties._giftwrap_for_handle) ||
            !productVariantIds.includes(String(item.properties._giftwrap_for_variant)) ||
            !productHandles.includes(item.properties._giftwrap_for_handle))
      );

      if (orphanedWraps.length) {
        await Promise.all(
          orphanedWraps.map(wrap =>
            fetch("/cart/change.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ id: wrap.key, quantity: 0 })
            })
          )
        );
        refreshCart();
      }
    } catch (err) {
      console.error("Gift wrap cleanup failed:", err);
    }
  }  

  // STEP 8 — Refresh cart section (AJAX only; no reloads)
  async function refreshCart() {
    try {
      // give /cart/change.js a moment to settle
      await new Promise(r => setTimeout(r, 250));

      // Ask the theme for fresh HTML of the sections we care about
      const res = await fetch(`${window.location.pathname}?sections=${CART_SECTIONS.join(",")}`, {
        method: "GET",
        headers: { "X-Requested-With": "XMLHttpRequest" }
      });

      if (!res.ok) {
        console.warn("Sections API not available; falling back to fragment swap.");
        // Fallback: fetch full page HTML and swap common containers (still AJAX)
        const html = await fetch(window.location.href + "?t=" + Date.now()).then(r => r.text());
        const doc = new DOMParser().parseFromString(html, "text/html");
        const selectors = [
          ".cart-wrapper",
          ".cart__content",
          "#cart-content",
          "form[action='/cart']",
          ".cart",
          "#CartDrawer .drawer__contents",
          "#CartDrawer"
        ];
        for (const sel of selectors) {
          const fresh = doc.querySelector(sel);
          const mount = document.querySelector(sel);
          if (fresh && mount) {
            mount.innerHTML = fresh.innerHTML;
          }
        }
        setTimeout(() => initGiftWrapCheckboxes(true), 80);
        return;
      }

      const json = await res.json(); // { "<sectionId>": "<html string>", ... }
      let updated = false;

      // Primary replacement: match the section wrapper by its standard id:
      //   <section id="shopify-section-<sectionId>">...</section>
      for (const [sectionId, html] of Object.entries(json)) {
        if (!html) continue;

        // Create a fragment for the returned section HTML
        const frag = document.createElement("div");
        frag.innerHTML = html.trim();
        const freshRoot =
          // Most themes return a <section id="shopify-section-..."> wrapper
          frag.querySelector(`#shopify-section-${sectionId}`) ||
          // Some return raw inner markup; take the first element
          frag.firstElementChild;

        // Try to find an existing mount by the standardized wrapper id
        let mount =
          document.getElementById(`shopify-section-${sectionId}`) ||
          // Fallbacks: theme-specific containers if not using section wrapper
          document.querySelector(`[data-section-id='${sectionId}']`) ||
          document.querySelector(`[data-section='${sectionId}']`);

        if (!mount) {
          // As a last resort, map a few known section IDs to common containers
          const manualTargets = {
            "main-cart-items": ["#main-cart-items", ".cart__items", "[data-cart-items]"],
            "cart-items": ["#cart-items", ".cart__items", "[data-cart-items]"],
            "cart-footer": ["#cart-footer", ".cart__footer", "[data-cart-footer]"],
            "cart-recommendations": ["#cart-recommendations", "[data-cart-recommendations]"],
            "cart-icon-bubble": ["cart-icon-bubble", "[data-cart-count-bubble]"],
            "cart-drawer": ["#CartDrawer .drawer__contents", "#CartDrawer"]
          };
          const guesses = manualTargets[sectionId] || [];
          for (const sel of guesses) {
            const candidate = document.querySelector(sel);
            if (candidate) {
              mount = candidate;
              break;
            }
          }
        }

        if (mount && freshRoot) {
          // If both are section wrappers, replace the whole node for cleanliness
          if (
            mount.id?.startsWith("shopify-section-") &&
            freshRoot.id?.startsWith("shopify-section-")
          ) {
            mount.replaceWith(freshRoot);
          } else {
            // Otherwise, just replace inner HTML
            mount.innerHTML = freshRoot.innerHTML || html;
          }
          updated = true;
        }
      }

      // Rebind any dynamic listeners after DOM swap
      if (updated) {
        setTimeout(() => initGiftWrapCheckboxes(true), 80);
      } else {
        console.warn("No cart containers matched for section replacement (AJAX).");
      }
    } catch (err) {
      console.error("AJAX cart refresh failed:", err);
      // Intentionally no hard reload — stays AJAX-only per your requirement.
    }
  }

  // STEP 9 — Observe cart updates
  document.addEventListener("theme:cart:updated", async () => {
    // After theme/cart updates, reconcile and re-init
    const cart = await fetch("/cart.js").then(r => r.json());
    await reconcileGiftWrapQty(cart);
    setTimeout(initGiftWrapCheckboxes, 200);
  });

  const observer = new MutationObserver(mutations => {
    for (const m of mutations) {
      if (
        Array.from(m.addedNodes).some(node =>
          node.nodeType === 1 &&
          (node.classList?.contains("cart__item") || node.querySelector?.(".cart__item"))
        )
      ) {
        setTimeout(initGiftWrapCheckboxes, 100);
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });

  // STEP 10 — Auto-sync when quantity inputs/selects change in the cart UI
  document.addEventListener("change", async (e) => {
    const inCartItem = e.target.closest(".cart__item");
    if (!inCartItem) return;

    // Common qty controls (inputs/selects named 'updates[...]')
    const isQtyControl = e.target.matches("input[name^='updates'], input[type='number'], select[name^='updates']");
    if (!isQtyControl) return;

    // Allow theme to process the change first
    setTimeout(async () => {
      const cart = await fetch("/cart.js").then(r => r.json());
      await reconcileGiftWrapQty(cart);
      refreshCart();
    }, 300);
  });

})();
